import AppConfig from "../../../config/config";
import { MediaContentType, MediaRecord } from "../../../interfaces/MediaObject";
import genericUtility from "../../utility/GenericUtility";
import mediaUtility from "../../utility/MediaUtility";

// Bucket that backs business/product media.
const STORAGE_BUCKET = "product-images";

/** Builds a permanent public URL from a storage object path. Exported for the
 *  AI batch converter, whose items carry bare objectPaths. */
export function publicUrlFromObjectPath(objectPath: string): string {
  // Representative-catalog images (see RepresentativeImageApiService /
  // ProductDraftMediaHelper.newRepresentativeDraftMediaItem) are persisted
  // with their full, already-public URL AS the objectPath — there's no
  // per-product bucket path to rebuild for those, and they may not even live
  // in STORAGE_BUCKET. Pass an absolute URL through untouched instead of
  // wrongly re-prefixing it.
  if (mediaUtility.isAbsoluteUrl(objectPath)) return objectPath;
  return `${AppConfig.supabaseStorageDomain}/storage/v1/object/public/${STORAGE_BUCKET}/${objectPath}`;
}

/**
 * Normalizes one raw gallery entry into a MediaRecord. The backend may send:
 *  - a bare URL string (legacy / image-only galleries), or
 *  - an object carrying mediaType / objectPath / publicUrl / section in either
 *    camelCase or snake_case.
 * Missing fields are derived: objectPath from the URL, publicUrl from the path,
 * and mediaType from the file extension (default image/jpeg). Returns null only
 * when nothing usable can be derived.
 */
export function convertRawMedia(raw: unknown): MediaRecord | null {
  // Case 1: a plain URL string.
  if (typeof raw === "string") {
    const url = raw;
    const objectPath = genericUtility.objectPathFromSignedUrl(url) ?? "";
    const mediaType =
      mediaUtility.contentTypeFromName(objectPath || url) ?? ("image/jpeg" as MediaContentType);
    return { mediaType, objectPath, publicUrl: url };
  }

  // Case 2: a structured object.
  if (raw && typeof raw === "object") {
    const r = raw as Record<string, any>;
    const objectPath: string =
      r.objectPath ?? r.object_path ?? r.path ?? genericUtility.objectPathFromSignedUrl(r.publicUrl ?? r.public_url ?? r.url ?? "") ?? "";

    const publicUrl: string =
      r.publicUrl ??
      r.public_url ??
      r.url ??
      (objectPath ? publicUrlFromObjectPath(objectPath) : "");

    if (!publicUrl) return null;

    const explicitType: string | undefined =
      r.mediaType ?? r.media_type ?? r.contentType ?? r.content_type;
    let mediaType: MediaContentType;
    if (explicitType && mediaUtility.isAllowedContentType(explicitType)) {
      mediaType = explicitType;
    } else {
      mediaType = mediaUtility.contentTypeFromName(objectPath || publicUrl) ?? "image/jpeg";
    }

    const section: string | undefined = r.section ?? r.sectionName ?? r.section_name ?? undefined;
    const thumbnailUrl: string | undefined = r.thumbnailUrl ?? r.thumbnail_url ?? undefined;

    return {
      mediaType,
      objectPath,
      publicUrl,
      ...(section ? { section } : {}),
      ...(thumbnailUrl ? { thumbnailUrl } : {}),
    };
  }

  return null;
}

/** Normalizes the `contents` array of a gallery response into MediaRecords. */
export function convertRawMediaList(contents: unknown): MediaRecord[] {
  if (!Array.isArray(contents)) return [];
  return contents
    .map(convertRawMedia)
    .filter((m): m is MediaRecord => m !== null);
}

export default { convertRawMedia, convertRawMediaList };
